import { useMemo, useState } from "react";
import { Alert, Box, Button, Card, CircularProgress, Typography } from "@mui/material";
import { Download, PictureAsPdf } from "@mui/icons-material";
import DashboardLayout from "app/layout/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/layout/DashboardNavbar";
import useUsoVip from "./hooks/useUsoVip";
import HeaderFrecuencia from "../frecuencia/components/HeaderFrecuencia";
import UsoVipFilters from "./components/UsoVipFilters";
import UsoVipTable from "./components/UsoVipTable";
import VipUsageChart from "./components/VipUsageChart";
import { visibleVipWarnings } from "./visibleWarnings";
import ReportCompanyHeader from "../frecuencia/components/ReportCompanyHeader";
import { formatReportDate } from "modules/came/model/frequencyModel";

export default function UsoVip() {
  const model = useUsoVip();
  const warnings = visibleVipWarnings(model.report);
  const visibleReport = useMemo(
    () =>
      model.report
        ? {
            ...model.report,
            history: model.report.history.filter(
              (row) => row.isVipVisit || (model.report.source === "vip-events" && row.isVipMovement)
            ),
          }
        : null,
    [model.report]
  );
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const exportReport = async (type) => {
    if (!model.report || exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const exports = await import("./reportExports");
      if (type === "pdf") exports.downloadVipUsagePdf(visibleReport, "history");
      else exports.downloadVipUsageCsv(visibleReport, "history");
    } catch (error) {
      setExportError(error.message || "No se pudo generar la descarga.");
    } finally {
      setExporting(false);
    }
  };
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ py: 3 }}>
        <HeaderFrecuencia
          title="Uso de VIP"
          subtitle="Historial de entradas y salidas del parqueadero VIP"
          summaryLabel="Ingresos VIP"
          loading={model.loading}
          onRefresh={model.refresh}
          summary={
            model.report?.vipSummary
              ? {
                  totalVisits: model.report.vipSummary?.total || 0,
                  totalDays: new Set(
                    model.report.history
                      .filter((row) => row.isVipVisit)
                      .map((row) => row.vipEntryDate)
                  ).size,
                }
              : null
          }
        >
          <UsoVipFilters
            filters={model.filters}
            groups={model.groups}
            onChange={model.updateFilters}
            onApply={model.applyFilters}
            onReset={model.resetFilters}
            loading={model.loading}
          />
        </HeaderFrecuencia>
        {model.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {model.error}
          </Alert>
        )}
        {exportError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {exportError}
          </Alert>
        )}
        {warnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2, fontSize: 13 }}>
            {warnings.join(" ")}
          </Alert>
        )}
        <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
          <ReportCompanyHeader />
          {!model.loading && model.report && (
            <VipUsageChart
              summary={model.report.vipSummary}
              direct={model.report.source === "vip-events"}
            />
          )}
          <Box
            sx={{
              p: { xs: 2, sm: 3 },
              pb: 0,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {model.report?.source === "vip-events"
                  ? "Movimientos de ingreso y salida VIP por placa"
                  : "Ciclos de ingreso y salida por placa"}
              </Typography>
              {model.report && (
                <Typography variant="body2" color="text.secondary">
                  {formatReportDate(model.appliedFilters.startDate)} 00:00 a{" "}
                  {formatReportDate(model.appliedFilters.endDate)} 23:59 · Hora de Colombia
                </Typography>
              )}
              {model.report && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {model.appliedFilters.plate ? `Placa: ${model.appliedFilters.plate} · ` : ""}
                  Grupo:{" "}
                  {model.groups.find(
                    (g) => String(g.idgroup) === String(model.appliedFilters.groupId)
                  )?.name || "Todos"}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={exporting ? <CircularProgress size={14} /> : <PictureAsPdf />}
                disabled={model.loading || !model.report || exporting}
                onClick={() => exportReport("pdf")}
                sx={{ color: "info.main", borderColor: "info.main" }}
              >
                PDF
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Download />}
                disabled={model.loading || !model.report || exporting}
                onClick={() => exportReport("csv")}
                sx={{ color: "info.main", borderColor: "info.main" }}
              >
                CSV
              </Button>
            </Box>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ px: { xs: 2, sm: 3 }, pt: 2 }} color="text.secondary">
              {model.report?.source === "vip-events"
                ? "Se muestran los movimientos registrados en VIP. Si falta el ingreso o la salida, aparece Sin información y no se calcula permanencia."
                : "Se muestran únicamente los ingresos a VIP."}
            </Typography>
            <UsoVipTable report={visibleReport} view="history" loading={model.loading} />
          </Box>
        </Card>
      </Box>
    </DashboardLayout>
  );
}
